/**
 * Appointment reminder config — facility-scoped hours-before-appointment SMS setting.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';

export interface AppointmentReminderConfig {
  id: number;
  facilityId: number;
  reminderHours: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: AppointmentReminderConfig[];
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: AppointmentReminderConfig;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string };
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Appointment reminder config request failed';
}

class AppointmentReminderConfigService {
  async list(facilityId: number): Promise<{
    success: boolean;
    data?: AppointmentReminderConfig[];
    error?: string;
  }> {
    try {
      const response = await apiGet<ApiListResponse>(
        API_ENDPOINTS.APPOINTMENT_REMINDER_CONFIG.LIST(facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to load appointment reminder config',
        };
      }
      return { success: true, data: Array.isArray(body.data) ? body.data : [] };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async create(payload: {
    facilityId: number;
    reminderHours: number;
  }): Promise<{ success: boolean; data?: AppointmentReminderConfig; error?: string }> {
    try {
      const response = await apiPost<ApiSingleResponse>(
        API_ENDPOINTS.APPOINTMENT_REMINDER_CONFIG.CREATE,
        {
          facilityId: payload.facilityId,
          reminderHours: payload.reminderHours,
        }
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to create appointment reminder config',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async update(
    id: number,
    payload: { facilityId: number; reminderHours: number }
  ): Promise<{ success: boolean; data?: AppointmentReminderConfig; error?: string }> {
    try {
      const response = await apiPut<ApiSingleResponse>(
        API_ENDPOINTS.APPOINTMENT_REMINDER_CONFIG.UPDATE(id),
        {
          facilityId: payload.facilityId,
          reminderHours: payload.reminderHours,
        }
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to update appointment reminder config',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async remove(id: number, facilityId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiDelete<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.APPOINTMENT_REMINDER_CONFIG.DELETE(id, facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to delete appointment reminder config',
        };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }
}

export const appointmentReminderConfigService = new AppointmentReminderConfigService();
export default appointmentReminderConfigService;
